import { useState, type ReactNode } from 'react';
import { Loader2, Pencil } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { DiscardChangesDialog } from '@/components/ui/discard-changes-dialog';
import ConfigurationSetting from '../pms-configuration/ConfigurationSetting';
import { VendorPageHeader } from '../pms-configuration/components/VendorPageHeader';
import { VENDOR_TAB_KEYS, VendorTabNav } from '../pms-configuration/components/VendorTabNav';
import { ConnectionFailedAlert } from './ConnectionFailedAlert';
import { integrationStatusCopy as copy } from './status/copy';
import type { VendorDefinition } from './types';
import type { UseVendorSettingsResult } from './useVendorSettings';

interface VendorSettingsLayoutProps {
  vendor: VendorDefinition;
  state: UseVendorSettingsResult;
  /** PR B: status pill, rendered right of the header title. */
  statusSlot?: ReactNode;
  /** PR B: disabled-state banner, rendered above the tabs card. */
  beforeTabs?: ReactNode;
  /** PR B: Danger Zone card, rendered below Configuration Settings. */
  afterSettings?: ReactNode;
}

/**
 * Shared skeleton for every vendor settings page: header · tabs (+ Edit
 * Settings / Cancel) · inline failure alert · vendor connection panel ·
 * configuration settings · unsaved-changes bar · dialogs. Everything
 * vendor-specific is read off `vendor` (see vendorRegistry).
 */
export const VendorSettingsLayout = ({
  vendor,
  state,
  statusSlot,
  beforeTabs,
  afterSettings,
}: VendorSettingsLayoutProps) => {
  const {
    ctx,
    selectedTab,
    selectTab,
    isConfigLinkClicked,
    blocker,
    isSaving,
    onBack,
    handleToggle,
    pendingToggle,
    confirmPendingToggle,
    cancelPendingToggle,
  } = state;
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const isCredentialsTab = selectedTab === VENDOR_TAB_KEYS.CREDENTIALS;
  const isReadMode = ctx.mode === 'read';
  // A credential test / save is in flight. The bar already refuses Discard and
  // Save while this is true; Cancel has to refuse too (review finding 2).
  // Discarding mid-test does not cancel the request, and the in-flight
  // `submitCredentials` closure still holds the pre-discard `editedSettings` —
  // so on success it would persist exactly what the user just discarded, and on
  // failure it would raise a red "Connection failed" alert over the read-only
  // summary.
  const isValidating = isSaving || ctx.toggleLoader;
  const hasStoredRecord = ctx.vendorData !== null;
  const { ConnectionPanel, OverviewTab, ToggleConfirmModal } = vendor;

  // Spec §5.2: Cancel with edits asks first; clean exits directly. Not shown
  // on first connection — there is no read view to return to.
  const onCancelClick = () => {
    if (isValidating) return;
    if (ctx.isDirty) setCancelConfirmOpen(true);
    else ctx.exitEdit();
  };
  const confirmCancel = () => {
    setCancelConfirmOpen(false);
    ctx.discardEdits();
  };

  // `size="sm"` + `h-8` is 32px at lg and up, which is the prototype's height.
  // Below lg the size's own `max-lg:h-11` still applies — `h-8` does not
  // override it, since tailwind-merge treats a responsive variant as a separate
  // key — so the controls grow to the 44px tap-target floor the e2e viewport
  // suite enforces. Deliberate, not a leak.
  //
  // Prototype: the tab bar's trailing slot is the page's only save surface —
  // Edit Settings in read mode, Cancel + Save in edit. Cancel and Save are
  // independently gated (the prototype flags them separately): Cancel needs a
  // read view to return to, Save needs something to save.
  const tabControls = isCredentialsTab ? (
    isReadMode ? (
      <Button type="button" variant="outline" size="sm" className="h-8" onClick={ctx.enterEdit}>
        <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
        {copy.editMode.edit}
      </Button>
    ) : (
      <>
        {hasStoredRecord && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8"
            disabled={isValidating}
            onClick={onCancelClick}
          >
            {copy.editMode.cancel}
          </Button>
        )}
        {ctx.isDirty && (
          <Button
            type="button"
            size="sm"
            className="h-8"
            disabled={isValidating || (vendor.saveDisabled?.(ctx) ?? false)}
            onClick={ctx.requestSubmit}
          >
            {isValidating && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
            {isValidating ? copy.saveLabels.validating : vendor.saveLabel(ctx)}
          </Button>
        )}
      </>
    )
  ) : null;

  // Only the bootstrap discards the tree now: the panel has nothing to mount
  // with yet (vendorData is still null pre-bootstrap), and no panel has raised
  // any flag yet, so there is no self-gating hazard — nothing is lost by not
  // rendering it. `isSaving` used to swap the page for a spinner too; the
  // bar's "Validating…" state owns that feedback now (spec §5.5) and the form
  // must stay mounted so a failure renders inline.
  //
  // isPanelBusy is different in kind, not degree: it means an ALREADY-MOUNTED
  // panel is doing vendor-owned async work (Buildium's OAuth save / sync-log
  // follow-on) and needs the page-level spinner up while it finishes. A
  // component must never gate its own mounting on a flag only it raises — if
  // this branch also unmounted <ConnectionPanel>, the very act of going busy
  // would tear the panel down, its cleanup would run, and its own effect's
  // `cancelled` guard would then block the busy flag from ever being lowered
  // again (permanently stranding the page in a spinner). So isPanelBusy hides
  // the normal chrome behind the spinner via CSS instead of discarding the
  // tree — ConnectionPanel stays mounted underneath, able to finish its work
  // and clear the flag itself.
  if (ctx.isBootstrapping) {
    return (
      <section className="flex flex-col min-h-full">
        <div className="flex items-center justify-center py-20">
          <Spinner size="lg" />
        </div>
      </section>
    );
  }

  return (
    <section className="flex flex-col min-h-full">
      {ctx.isPanelBusy && (
        <div className="flex flex-1 items-center justify-center py-20">
          <Spinner size="lg" />
        </div>
      )}

      <div className={ctx.isPanelBusy ? 'hidden' : 'flex flex-col flex-1'}>
        <VendorPageHeader
          vendorName={vendor.displayName}
          vendorLogo={vendor.logo}
          logoHeightPx={vendor.logoHeightPx}
          onBack={onBack}
          trailing={statusSlot}
        />

        {beforeTabs}

        {/* Prototype: tabs + tab content live in one 12px card; the Danger
            Zone is its own card below it. */}
        <div className="flex-1">
          <div className="m-5 rounded-xl border border-border bg-card py-5">
            <VendorTabNav
              selectedTab={selectedTab}
              onSelectTab={selectTab}
              className="px-5"
              trailing={tabControls}
            />
            <div className="pt-4">
              {isCredentialsTab && (
                <>
                  {ctx.credentialFailure && (
                    <div className="px-4">
                      <ConnectionFailedAlert
                        vendorName={vendor.displayName}
                        kind={ctx.credentialFailure}
                      />
                    </div>
                  )}
                  <ConnectionPanel ctx={ctx} />
                  {vendor.showSettings(ctx) && (
                    // Spec §5.3: read mode dims the card and disables the toggles.
                    // Opacity only — no pointer-events-none — so the watermark
                    // tooltip trigger keeps working.
                    //
                    // Deliberately NOT gated on `ctx.toggleLoader` (review
                    // finding 3). That gate was written when `isSaving` blanked
                    // the whole page for a spinner, so it never actually showed;
                    // with the page staying up it made the card vanish and the
                    // layout collapse for the length of every credential test,
                    // and remounting it on a failed test re-ran
                    // `ConfigurationSetting`'s DV-1958 normalizer against the
                    // current `editedSettings` — the exact write the `mode`
                    // gating of `isDirty` exists to defend against. The card
                    // stays mounted and goes inert instead.
                    <div className={isReadMode ? 'opacity-70' : undefined}>
                      <ConfigurationSetting
                        handleChange={handleToggle}
                        editedResponse={ctx.editedSettings}
                        setEditedResponse={ctx.setEditedSettings}
                        isDisabled={isReadMode || isValidating || vendor.settingsDisabled(ctx)}
                        pmsSourceName={vendor.displayName}
                      />
                    </div>
                  )}
                </>
              )}
              {selectedTab === VENDOR_TAB_KEYS.OVERVIEW && (
                <OverviewTab isHowToConfigLinkClicked={isConfigLinkClicked} />
              )}
            </div>
          </div>
          {isCredentialsTab && afterSettings}
        </div>

      </div>

      <DiscardChangesDialog
        open={blocker.isBlocking}
        onKeepEditing={blocker.cancel}
        onDiscard={blocker.proceed}
      />
      <DiscardChangesDialog
        open={cancelConfirmOpen}
        onKeepEditing={() => setCancelConfirmOpen(false)}
        onDiscard={confirmCancel}
      />

      {ToggleConfirmModal && (
        <ToggleConfirmModal
          open={pendingToggle !== null}
          onConfirm={confirmPendingToggle}
          onCancel={cancelPendingToggle}
        />
      )}
    </section>
  );
};
